import { type FormEvent, type ReactNode, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { api, jsonBody } from "../api/client";
import { BrandLockup } from "../brand/BrandMark";
import { LegalLine } from "../legal/LegalFooter";
import { ErrorPanel, Field, Notice } from "../ui/components";

interface AcceptedMessage {
  message: string;
}

function RecoveryFrame({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <main className="auth-layout">
      <section className="auth-brand">
        <BrandLockup />
        <div>
          <h1>Secure account recovery.</h1>
          <p>
            Reset links are short-lived, single-use, and do not reveal whether an account exists.
          </p>
        </div>
      </section>
      <section className="auth-panel" aria-labelledby="recovery-title">
        <div className="auth-card">
          <h1 id="recovery-title">{title}</h1>
          <p>{description}</p>
          {children}
        </div>
        <LegalLine />
      </section>
    </main>
  );
}

export function ForgotPasswordPage() {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [message, setMessage] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setWorking(true);
    setError(undefined);
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<AcceptedMessage>("/api/v1/auth/password/forgot", {
        method: "POST",
        body: jsonBody({ email: String(form.get("email") ?? "") }),
      });
      setMessage(result.message);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  return (
    <RecoveryFrame
      title="Forgot your password?"
      description="Enter the email address used for your staff or platform account."
    >
      {error ? <ErrorPanel error={error} /> : null}
      {message ? (
        <Notice tone="success" title="Check your email">
          {message}
        </Notice>
      ) : (
        <form onSubmit={submit}>
          <Field label="Email address">
            <input name="email" type="email" autoComplete="email" required />
          </Field>
          <button className="button primary" type="submit" disabled={working}>
            {working ? "Requesting…" : "Send reset link"}
          </button>
        </form>
      )}
      <Link className="button ghost" to="/login">
        Back to sign in
      </Link>
    </RecoveryFrame>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // The reset link carries its token in the fragment, which never leaves the browser: it is not
  // sent to the server, logged, or passed on in a Referer header. The query form is still read so
  // a link issued before the change keeps working.
  const token =
    new URLSearchParams(location.hash.replace(/^#/u, "")).get("token") ??
    searchParams.get("token") ??
    "";
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [complete, setComplete] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const newPassword = String(form.get("newPassword") ?? "");
    if (newPassword !== String(form.get("confirmPassword") ?? "")) {
      setError(new Error("The new password and confirmation do not match."));
      return;
    }
    setWorking(true);
    setError(undefined);
    try {
      await api<void>("/api/v1/auth/password/reset", {
        method: "POST",
        body: jsonBody({ token, newPassword }),
      });
      setComplete(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  return (
    <RecoveryFrame
      title="Choose a new password"
      description="Choose at least 12 characters that nobody could guess. This reset signs out every existing session."
    >
      {error ? <ErrorPanel error={error} /> : null}
      {!token ? (
        <Notice tone="danger">This reset link is missing its token.</Notice>
      ) : complete ? (
        <Notice tone="success" title="Password changed">
          Your password was reset and existing sessions were revoked.
        </Notice>
      ) : (
        <form onSubmit={submit}>
          <Field
            label="New password"
            hint="At least 12 characters. Not a common password, a keyboard pattern, or your own name or email address."
          >
            <input
              name="newPassword"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
            />
          </Field>
          <Field label="Confirm new password">
            <input
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
            />
          </Field>
          <button className="button primary" type="submit" disabled={working || !token}>
            {working ? "Resetting…" : "Reset password"}
          </button>
        </form>
      )}
      <Link className="button ghost" to="/login">
        Continue to sign in
      </Link>
    </RecoveryFrame>
  );
}
